import { useQuery } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  Breadcrumbs,
  BreadcrumbsItem,
  ButtonLink,
  Dot,
  Heading,
  Input,
  MultiSelect,
  NavigationTabs,
  NavigationTabsItem,
  Paragraph,
  Pill,
  PillKind,
  Select,
  Spinner,
  Stack,
  Table,
  TableColumns,
  Text,
} from '@sixfold/common-ui';
import React, { useMemo, useState } from 'react';
import { Navigate, Route, Routes as RouterRoutes, useParams } from 'react-router';

import { Card, CheckIcon, DefinitionList, EmptyState, HealthPill, TourStatusPill } from './ui';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { formatDateWithTimeZone } from '../../lib/date';
import {
  notEmpty,
  MobileTourDetailsQuery,
  MobileTourDetailsQueryVariables,
  MobileTourSignalsQuery,
} from '../../lib/graphql';
import { Routes } from '../../routes';
import { Diagnosis, diagnoseTour, getDriverLoginEvents, getLoginAppLabel, getPrimaryAllocation } from '../diagnostics';
import { mobileTourDetailsQuery } from '../graphql';
import { useKafkaLogs } from '../kafka_logs';
import { getCloudLoggingUrl } from '../logs_link';
import { useMobileTourSignals } from '../signals';
import { LOG_SOURCES, LogEntry, LogSource } from '../types';

import styles from './mobile.module.css';

type TourDetails = NonNullable<MobileTourDetailsQuery['tour']>;

const TOUR_EVENT_LABEL: Partial<
  Record<string, { name: string; severity: LogEntry['severity']; significant: boolean }>
> = {
  TRUCK_ALLOCATION: { name: 'Vehicle allocated', severity: 'info', significant: true },
  DEALLOCATION: { name: 'Vehicle deallocated', severity: 'warning', significant: true },
  TRACKING_START: { name: 'Sixfold tracking started', severity: 'success', significant: true },
  VISIBLE_TRACKING_START: { name: 'Visible tracking started', severity: 'success', significant: true },
  EVENT_STREAM_STARTED: { name: 'Event stream started', severity: 'info', significant: false },
  SHIPPER_VISIBLE: { name: 'Visible to shipper', severity: 'info', significant: false },
  WARNING_TRIGGERED: { name: 'Warning raised', severity: 'warning', significant: true },
  WARNING_ENDED: { name: 'Warning cleared', severity: 'info', significant: false },
  COMPLETE: { name: 'Tour completed', severity: 'success', significant: true },
  ABORT: { name: 'Tour aborted', severity: 'danger', significant: true },
  ABANDON: { name: 'Tour abandoned', severity: 'danger', significant: true },
  FAILED: { name: 'Tour failed', severity: 'danger', significant: true },
};

const SEVERITY_DOT: Record<LogEntry['severity'], 'info' | 'success' | 'warning' | 'danger'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
};

const SOURCE_KIND: Record<LogSource, PillKind> = {
  Tour: 'info',
  'Driver app': 'success',
  Notification: 'warning',
  Kafka: 'neutral',
};

const humanize = (value: string) => value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');

const buildLoginLogs = (tour: TourDetails): LogEntry[] =>
  getDriverLoginEvents(tour).map((event) => ({
    id: `login:${event.timestamp}:${event.externalEventSource ?? ''}`,
    timestamp: event.timestamp,
    source: 'Driver app',
    name: 'Driver logged in',
    details: getLoginAppLabel(event),
    significant: true,
    severity: 'success',
  }));

const buildTourLogs = (tour: TourDetails): LogEntry[] => {
  const stopNumber = new Map(tour.stops.map((stop) => [stop.stop_id, stop.display_sequence_number]));
  return tour.events
    .map((event): LogEntry | null => {
      if (event.event_time === null) return null;
      if (event.__typename === 'TourEvent') {
        const known = TOUR_EVENT_LABEL[event.event_name];
        return {
          id: `tour:${event.event_id}`,
          timestamp: event.event_time,
          source: 'Tour',
          name: known?.name ?? humanize(event.event_name),
          details:
            event.vehicle_lat !== null && event.vehicle_lng !== null
              ? `At ${event.vehicle_lat.toFixed(4)}, ${event.vehicle_lng.toFixed(4)}`
              : event.event_name,
          significant: known?.significant ?? false,
          severity: known?.severity ?? 'info',
        };
      }
      return {
        id: `stop:${event.event_id}`,
        timestamp: event.event_time,
        source: 'Tour',
        name: `${humanize(event.stopEventName)} stop ${stopNumber.get(event.stop_id) ?? ''}`.trim(),
        details: event.stopEventName,
        significant: false,
        severity: 'info',
      };
    })
    .filter(notEmpty);
};

const useTourLogs = (
  tour: TourDetails,
  signalLogs: LogEntry[],
): { logs: LogEntry[]; kafkaLoading: boolean; kafkaFailed: boolean } => {
  const anchor =
    tour.vehicleAllocations.find(({ allocatedAt }) => allocatedAt !== null)?.allocatedAt ?? tour.start_time;
  const kafka = useKafkaLogs(tour.tour_id, anchor);
  const logs = useMemo(
    () =>
      [...buildTourLogs(tour), ...buildLoginLogs(tour), ...signalLogs, ...kafka.logs].sort((a, b) =>
        a.timestamp.localeCompare(b.timestamp),
      ),
    [tour, signalLogs, kafka.logs],
  );
  return { logs, kafkaLoading: kafka.loading, kafkaFailed: kafka.error !== undefined };
};

const DiagnosisAlert: React.FC<{ diagnosis: Diagnosis }> = ({ diagnosis }) => {
  if (diagnosis.health === 'upcoming' && diagnosis.issue === null) {
    return (
      <Alert kind="info">
        <AlertTitle>Tour has not started yet</AlertTitle>
        <Paragraph>Checks that depend on the driver will update once the tour starts.</Paragraph>
      </Alert>
    );
  }
  if (diagnosis.issue === null) {
    return (
      <Alert kind="success">
        <AlertTitle>No issues found</AlertTitle>
        <Paragraph>
          {diagnosis.unknownChecks.length === 0
            ? 'Every check passed.'
            : `Every check with data passed. No data for: ${diagnosis.unknownChecks.map(({ label }) => label).join(', ')}.`}
        </Paragraph>
      </Alert>
    );
  }
  return (
    <Alert kind={diagnosis.health === 'failing' ? 'danger' : 'warning'}>
      <AlertTitle>Likely issue: {diagnosis.issue.title}</AlertTitle>
      <Paragraph>{diagnosis.issue.detail}</Paragraph>
      <Paragraph>
        <Text weight="bold">Next step: </Text>
        {diagnosis.issue.nextStep}
      </Paragraph>
    </Alert>
  );
};

const ChecklistCard: React.FC<{ diagnosis: Diagnosis }> = ({ diagnosis }) => (
  <Card title="Troubleshooting checklist" subtitle="In the order a driver goes through them">
    <div>
      {diagnosis.checks.map((check) => (
        <div key={check.id} className={styles.checkRow}>
          <CheckIcon status={check.status} />
          <div className={styles.cellStack}>
            <Text weight="bold">{check.label}</Text>
            <Text size="small" prominence="subtle">
              {check.detail}
            </Text>
          </div>
          <Text size="small" prominence="subtle">
            {check.source}
          </Text>
        </div>
      ))}
    </div>
  </Card>
);

const AllocationCard: React.FC<{ tour: TourDetails }> = ({ tour }) => {
  const { timeZone } = useTimeZone();
  const format = (value: string | null | undefined) => (value ? formatDateWithTimeZone(value, timeZone) : '-');
  const allocation = getPrimaryAllocation(tour.vehicleAllocations);
  return (
    <Card title="Allocation">
      {allocation === undefined ? (
        <Text prominence="subtle">No vehicle or phone allocated</Text>
      ) : (
        <DefinitionList
          items={[
            { label: 'Device type', value: allocation.allocationDeviceType ?? '-' },
            { label: 'Vehicle', value: allocation.vehicleLicensePlateNumber ?? '-' },
            { label: 'Allocated at', value: format(allocation.allocatedAt) },
            { label: 'Tracking from', value: format(allocation.startTrackingAt) },
            { label: 'Auto allocation', value: allocation.isAutoAllocation === true ? 'Yes' : 'No' },
            {
              label: 'Position sources',
              value: tour.visibilitySources.map(({ primaryEventSource }) => primaryEventSource).join(', ') || 'None',
            },
          ]}
        />
      )}
    </Card>
  );
};

type MobileSession = NonNullable<NonNullable<MobileTourSignalsQuery['tour']>['mobileSession']>;

const SessionCard: React.FC<{ session: MobileSession | null }> = ({ session }) => {
  const { timeZone } = useTimeZone();
  return (
    <Card title="Trimble Freight Visibility session" subtitle="Registered when the driver logs in with the new app">
      {session === null ? (
        <Text prominence="subtle">No Trimble Freight Visibility session for this tour</Text>
      ) : (
        <DefinitionList
          items={[
            { label: 'Session started', value: formatDateWithTimeZone(session.createdAt, timeZone) },
            {
              label: session.isActive ? 'Expires' : 'Expired',
              value: formatDateWithTimeZone(session.expiresAt, timeZone),
            },
            { label: 'Push platform', value: session.pushPlatform ?? '-' },
            { label: 'App version', value: session.pushAppVersion ?? '-' },
            { label: 'Push notifications', value: session.hasPushToken ? 'Registered' : 'Not registered' },
          ]}
        />
      )}
    </Card>
  );
};

const Timeline: React.FC<{ entries: LogEntry[] }> = ({ entries }) => {
  const { timeZone } = useTimeZone();
  if (entries.length === 0) {
    return <EmptyState title="No events yet" description="Events appear here once the tour is allocated." />;
  }
  return (
    <div className={styles.timeline}>
      {entries.map((entry) => (
        <div key={entry.id} className={styles.timelineItem}>
          <Text size="small" className={styles.timelineTime}>
            {formatDateWithTimeZone(entry.timestamp, timeZone)}
          </Text>
          <div className={styles.timelineRail}>
            <Dot kind={SEVERITY_DOT[entry.severity]} />
          </div>
          <div className={styles.timelineBody}>
            <Stack alignItems="center" gap="small">
              <Text weight="bold">{entry.name}</Text>
              <Pill kind={SOURCE_KIND[entry.source]}>{entry.source}</Pill>
            </Stack>
            <Text size="small" prominence="subtle">
              {entry.details}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

const OverviewTab: React.FC<{
  tour: TourDetails;
  diagnosis: Diagnosis;
  logs: LogEntry[];
  session: MobileSession | null;
}> = ({ tour, diagnosis, logs, session }) => {
  const [showAll, setShowAll] = useState(false);
  const entries = showAll ? logs : logs.filter((entry) => entry.significant);
  return (
    <div className={styles.detailLayout}>
      <Stack direction="column" wrap={false} gap="medium">
        <ChecklistCard diagnosis={diagnosis} />
        <AllocationCard tour={tour} />
        <SessionCard session={session} />
      </Stack>
      <Card
        title="Timeline"
        subtitle={showAll ? 'All events from every source' : 'Key events from the tour and the driver app'}
        actions={
          <Select
            label="Events"
            labelPosition="hidden"
            name="timeline-scope"
            size="small"
            value={showAll ? 'all' : 'key'}
            options={[
              { value: 'key', label: 'Key events' },
              { value: 'all', label: 'All events' },
            ]}
            onChange={(option) => setShowAll(option?.value === 'all')}
          />
        }>
        <Timeline entries={entries} />
      </Card>
    </div>
  );
};

const EventLogTab: React.FC<{ logs: LogEntry[] }> = ({ logs }) => {
  const { timeZone } = useTimeZone();
  const [sources, setSources] = useState<LogSource[]>([]);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const filtered = logs.filter(
    (entry) =>
      (sources.length === 0 || sources.includes(entry.source)) &&
      (needle === '' || `${entry.name} ${entry.details}`.toLowerCase().includes(needle)),
  );
  const sourceOptions = LOG_SOURCES.map((value) => {
    const count = logs.filter((entry) => entry.source === value).length;
    return { value, label: `${value} (${count})`, disabled: count === 0 };
  });
  const columns: TableColumns<LogEntry> = [
    {
      id: 'timestamp',
      title: 'Time',
      width: '150px',
      cellContent: ({ row }) => <span className={styles.mono}>{formatDateWithTimeZone(row.timestamp, timeZone)}</span>,
    },
    {
      id: 'source',
      title: 'Source',
      width: '190px',
      cellContent: ({ row }) => <Pill kind={SOURCE_KIND[row.source]}>{row.source}</Pill>,
    },
    {
      id: 'name',
      title: 'Event',
      width: '240px',
      cellContent: ({ row }) => (
        <Stack gap="xsmall" alignItems="center" wrap={false}>
          <Dot kind={SEVERITY_DOT[row.severity]} />
          <Text>{row.name}</Text>
        </Stack>
      ),
    },
    { id: 'details', title: 'Details' },
  ];

  return (
    <Card title="Event log" subtitle="Tour events, driver logins and Kafka messages in one place">
      <Stack direction="column" wrap={false} gap="medium">
        <div className={styles.filterBar}>
          <div className={styles.filterSearch}>
            <Input
              label="Search events"
              labelPosition="hidden"
              size="small"
              name="event-log-search"
              icon="small-search"
              placeholder="Event name or details"
              value={query}
              clearable
              onClear={() => setQuery('')}
              onChange={({ value }) => setQuery(value)}
            />
          </div>
          <div className={styles.filterMultiSelect}>
            <MultiSelect
              label="Sources"
              labelPosition="hidden"
              placeholder="All sources"
              clearAllLabel="Clear all"
              {...{ closeMenuOnSelect: false }}
              options={sourceOptions}
              value={sourceOptions.filter((option) => sources.includes(option.value))}
              onChange={(selected) =>
                setSources(Array.isArray(selected) ? selected.map(({ value }) => value as LogSource) : [])
              }
            />
          </div>
          <Text prominence="subtle">
            {filtered.length} of {logs.length} events
          </Text>
        </div>
        {filtered.length === 0 ? (
          <EmptyState title="No events match" description="Clear the search or pick another source." />
        ) : (
          <Table data={filtered} columns={columns} />
        )}
      </Stack>
    </Card>
  );
};

const TourHeader: React.FC<{ tour: TourDetails; diagnosis: Diagnosis }> = ({ tour, diagnosis }) => {
  const { timeZone } = useTimeZone();
  const tourId = String(tour.tour_id);
  const stops = tour.stops;
  const route =
    stops.length > 1
      ? `${stops[0].location?.name ?? 'Stop 1'} to ${stops[stops.length - 1].location?.name ?? `Stop ${stops.length}`}`
      : null;
  return (
    <Stack justifyContent="space-between" alignItems="start" gap="medium">
      <Stack direction="column" wrap={false} gap="xsmall">
        <Stack alignItems="center" gap="small">
          <Heading type="title" size="large">
            Tour {tourId}
          </Heading>
          <TourStatusPill status={tour.status} />
          <HealthPill health={diagnosis.health} />
        </Stack>
        <Text prominence="subtle">
          {[
            tour.shipper_transport_number ?? tour.transport_id,
            tour.carrier?.company_name,
            route,
            tour.start_time !== null ? `starts ${formatDateWithTimeZone(tour.start_time, timeZone)}` : null,
          ]
            .filter(notEmpty)
            .join(' · ')}
        </Text>
      </Stack>
      <Stack gap="small">
        {tour.carrier !== null && (
          <ButtonLink
            kind="neutral"
            size="small"
            iconStart="small-company"
            href={Routes.MobileCompany.generatePath({ company_id: tour.carrier.company_id })}>
            Carrier
          </ButtonLink>
        )}
        <ButtonLink
          kind="neutral"
          size="small"
          href={getCloudLoggingUrl([tourId, tour.shipper_transport_number])}
          target="_blank">
          API logs
        </ButtonLink>
        <ButtonLink
          kind="neutral"
          size="small"
          iconStart="small-truck"
          href={Routes.Tour.generatePath({ tour_id: tourId })}>
          Full tour view
        </ButtonLink>
      </Stack>
    </Stack>
  );
};

const TourPageContent: React.FC<{ tour: TourDetails }> = ({ tour }) => {
  const mobile = useMobileTourSignals(tour.tour_id);
  const diagnosis = useMemo(() => diagnoseTour({ ...tour, signals: mobile.signals }), [tour, mobile.signals]);
  const { logs, kafkaLoading, kafkaFailed } = useTourLogs(tour, mobile.logs);
  const base = Routes.MobileTour.generatePath({ tour_id: String(tour.tour_id) });
  return (
    <Stack direction="column" wrap={false} gap="medium">
      <TourHeader tour={tour} diagnosis={diagnosis} />
      <DiagnosisAlert diagnosis={diagnosis} />
      {kafkaFailed && (
        <Alert kind="warning">
          <AlertTitle>Kafka messages could not be loaded</AlertTitle>
          <Paragraph>Other events are still shown.</Paragraph>
        </Alert>
      )}
      <NavigationTabs>
        <NavigationTabsItem href={`${base}/overview`}>Overview</NavigationTabsItem>
        <NavigationTabsItem href={`${base}/log`}>
          Event log ({logs.length}
          {kafkaLoading ? '+' : ''})
        </NavigationTabsItem>
      </NavigationTabs>
      <RouterRoutes>
        <Route
          path="overview"
          element={<OverviewTab tour={tour} diagnosis={diagnosis} logs={logs} session={mobile.session} />}
        />
        <Route path="log" element={<EventLogTab logs={logs} />} />
        <Route index element={<Navigate to={`${base}/overview`} replace />} />
      </RouterRoutes>
    </Stack>
  );
};

export const TourPage: React.FC = () => {
  const { tour_id } = useParams() as { tour_id: string };
  const { data, loading, error } = useQuery<MobileTourDetailsQuery, MobileTourDetailsQueryVariables>(
    mobileTourDetailsQuery,
    { variables: { tour_id: parseInt(tour_id, 10) }, skip: Number.isNaN(parseInt(tour_id, 10)) },
  );

  return (
    <Stack direction="column" wrap={false} gap="medium">
      <Breadcrumbs label="Breadcrumbs">
        <BreadcrumbsItem href={Routes.MobileCommandCenter.generatePath({})}>Mobile Command Center</BreadcrumbsItem>
        <BreadcrumbsItem href={Routes.MobileTours.generatePath({})}>Tours</BreadcrumbsItem>
        <BreadcrumbsItem current="page">Tour {tour_id}</BreadcrumbsItem>
      </Breadcrumbs>
      {loading ? (
        <Stack justifyContent="center" padding="xlarge">
          <Spinner />
        </Stack>
      ) : error !== undefined ? (
        <Alert kind="danger">
          <AlertTitle>Could not load tour {tour_id}</AlertTitle>
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      ) : !data?.tour ? (
        <EmptyState title={`Tour ${tour_id} not found`} description="Check the tour ID and try again." />
      ) : (
        <TourPageContent tour={data.tour} />
      )}
    </Stack>
  );
};
